/* eslint-disable no-console */
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  collection,
  getDocs,
  addDoc,
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  setDoc,
} from "firebase/firestore";
import {
  Box,
  Button,
  Typography,
  Container,
  CircularProgress,
  Dialog,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
} from "@mui/material";

import { db } from "../firebase";
import Scrollbar from "../components/Scrollbar";
import TeamsScoreSheet from "../scoring/TeamsScoreSheet";
import IndividualsScoreSheet from "../scoring/IndividualsScoreSheet";

import PrintableScoreSheet from "../scoring/PrintableScoreSheet";
import EditTeams from "../pennant/EditTeams";

import generateTeamsDraw from "./generateTeamsDraw";
import generateDoublesDraw from "./generateDoublesDraw";
import generateSinglesDraw from "./generateSinglesDraw";

import TeamsDraw from "./TeamsDraw";
import DoublesDraw from "./DoublesDraw";
import SinglesDraw from "./SinglesDraw";

import EditDrawModal from "./EditDrawModal";

export default function Draws({ pennantId, seasonId }) {
  const [members, setMembers] = useState([]);
  const [draws, setDraws] = useState([]);
  const [teams, setTeams] = useState([]);
  const [existingDraw, setExistingDraw] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openScoreSheet, setOpenScoreSheet] = useState(false);
  const [openPrintableScoreSheet, setOpenPrintableScoreSheet] = useState(false);
  const [openEditTeams, setOpenEditTeams] = useState(false);
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [pennantData, setPennantData] = useState(null);
  const [teamsExist, setTeamsExist] = useState(false);
  const [openEditDrawModal, setOpenEditDrawModal] = useState(false);

  const refreshTeams = async () => {
    const teamsSnapshot = await getDocs(
      collection(db, "Pennants", pennantId, "teams")
    );
    const teamsList = teamsSnapshot.docs.map((docSnap) => ({
      id: docSnap.id,
      ...docSnap.data(),
    }));
    setTeams(teamsList);
    setTeamsExist(!teamsSnapshot.empty);
  };

  const getPennantDaysInRange = (startDate, endDate, dayOfWeek) => {
    const days = [];
    const currentDate = new Date(startDate);
    const end = new Date(endDate);

    while (currentDate.getDay() !== dayOfWeek) {
      currentDate.setDate(currentDate.getDate() + 1);
    }
    while (currentDate <= end) {
      days.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 7);
    }
    return days;
  };

  const handleOpenEditDrawModal = () => {
    if (!existingDraw) {
      alert("No draw to edit!");
      return;
    }
    setOpenEditDrawModal(true);
  };

  const handleSaveEditedDraw = async (updatedDraw) => {
    try {
      const drawDocRef = doc(db, "Pennants", pennantId, "draws", existingDraw.id);
      // Assume updatedDraw contains the updated weeks (or whatever structure you're using)
      await updateDoc(drawDocRef, { weeks: updatedDraw.weeks });
      setExistingDraw(updatedDraw); // update local state
      alert("Draw updated successfully!");
    } catch (err) {
      console.error("Error updating draw:", err);
      alert("Error updating draw.");
    }
  };

  useEffect(() => {
    const fetchMembersAndDraws = async () => {
      if (!pennantId) return;
      setLoading(true);

      try {
        const pennantRef = doc(db, "Pennants", pennantId);
        const pennantSnap = await getDoc(pennantRef);
        if (pennantSnap.exists()) {
          const pData = pennantSnap.data();
          setPennantData(pData);

          await refreshTeams();

          const memberIds = Array.isArray(pData.members)
            ? pData.members
            : Object.values(pData.members || {}).flat();
          const memberDetails = await Promise.all(
            memberIds.map(async (mid) => {
              const memSnap = await getDoc(doc(db, "members", mid));
              if (memSnap.exists()) {
                const d = memSnap.data();
                return {
                  id: mid,
                  name: `${d.firstName || "Unknown"} ${
                    d.lastName || "Member"
                  }`,
                  ...d,
                };
              }
              return { id: mid, name: "Unknown Member" };
            })
          );
          setMembers(memberDetails);

          const drSnapshot = await getDocs(
            collection(db, "Pennants", pennantId, "draws")
          );
          if (!drSnapshot.empty) {
            const existingDrawData = drSnapshot.docs.map((docSnap) => ({
              id: docSnap.id,
              ...docSnap.data(),
            }));

            setExistingDraw(existingDrawData[0]);
          } else {
            setExistingDraw(null);
          }
        }
      } catch (err) {
        console.error("Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchMembersAndDraws();
  }, [pennantId]);

  const generateDraw = async () => {
    if (!members.length) {
      alert("No members to generate a draw!");
      return;
    }
    setLoading(true);

    try {
      const pennantSnap = await getDoc(doc(db, "Pennants", pennantId));
      if (!pennantSnap.exists()) {
        alert("No valid pennant data!");
        return;
      }
      const {
        startDate,
        endDate,
        pennantDay,
        seasonName,
        participantType,
        playType,
        members: sectionObj,
      } = pennantSnap.data() || {};

      if (!startDate || !endDate || !pennantDay) {
        alert("Pennant data is incomplete (missing start/end date or day)!");
        return;
      }

      const validSeasonId = seasonName || "UnknownSeason";
      const dayOfWeek = [
        "Sunday",
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
      ].indexOf(pennantDay);
      if (dayOfWeek === -1) {
        alert("Invalid day of the week!");
        return;
      }

      const allDays = getPennantDaysInRange(
        new Date(startDate),
        new Date(endDate),
        dayOfWeek
      );
      if (!allDays.length) {
        alert("No valid match days found in this range!");
        return;
      }

      let weeklyDraws = [];

      if (participantType === "Teams") {
        weeklyDraws = await generateTeamsDraw(pennantId, allDays);
      }

      else if (participantType === "Individuals" && playType === "Doubles") {
        weeklyDraws = await generateDoublesDraw(pennantId, allDays);
      }

      else {
        weeklyDraws = generateSinglesDraw({
          pennantId,
          members,
          sectionObj,
          allDays,
        });
      }

      setDraws(weeklyDraws);
      await saveDraws(weeklyDraws, validSeasonId);
    } catch (error) {
      console.error("Error generating draw:", error);
      alert("Error generating draw. See console for details.");
    } finally {
      setLoading(false);
    }
  };

  const saveDraws = async (weeklyDraws, validSeasonId) => {
    if (!weeklyDraws.length) {
      alert("No draws to save!");
      return;
    }

    try {
      const drawsCol = collection(db, "Pennants", pennantId, "draws");
      const newDrawDocRef = await addDoc(drawsCol, {
        weeks: weeklyDraws,
        timestamp: new Date(),
      });

      alert("Draws saved successfully!");
    } catch (err) {
      console.error("Critical error saving draws:", err);
      alert("Error saving draws. Check console for details.");
    }
  };

  const deleteDraw = async () => {
    if (!existingDraw) {
      alert("No draw to delete!");
      return;
    }
    try {
      const drawDocRef = doc(db, "Pennants", pennantId, "draws", existingDraw.id);
      await deleteDoc(drawDocRef);
      setExistingDraw(null);
      alert("Draw deleted!");
    } catch (err) {
      console.error("Error deleting draw:", err);
    }
  };


  const generateTeams = async () => {
    if (!pennantData || pennantData.participantType !== "Teams") {
      alert("Invalid or not a Teams event!");
      return;
    }
    try {
      const sectionMembers = { ...pennantData.members };
      const sections = Object.keys(sectionMembers);

      const generatedTeams = [];
      const teamNames = [];

      const allMemberIds = Object.values(sectionMembers).flat();
      const memberDetailsArray = await Promise.all(
        allMemberIds.map(async (memberId) => {
          const snap = await getDoc(doc(db, "members", memberId));
          if (snap.exists()) {
            const data = snap.data();
            return {
              id: memberId,
              firstName: data.firstName || "Unknown",
              lastName: data.lastName || "Member",
              pennantRanking: data.pennantRanking || "NA",
            };
          }
          return null;
        })
      );

      const memberDetails = memberDetailsArray.reduce((acc, m) => {
        if (m) acc[m.id] = m;
        return acc;
      }, {});

      const section1Members = sectionMembers[1] || [];
      const section1Names = section1Members
        .map((id) => memberDetails[id]?.lastName || "Unknown")
        .filter((name) => name !== "Unknown");

      section1Names.forEach((lastName) => {
        let baseName = lastName;
        let count = 1;
        while (teamNames.includes(baseName)) {
          baseName = `${lastName} ${count}`;
          count += 1;
        }
        teamNames.push(baseName);
      });

      while (
        sections.some((sec) => (sectionMembers[sec]?.length || 0) > 0)
      ) {
        const team = {};
        sections.forEach((section) => {
          if (sectionMembers[section] && sectionMembers[section].length > 0) {
            const memberId = sectionMembers[section].shift();
            const mem = memberDetails[memberId];
            if (mem) {
              team[section] = { ...mem, section };
            } else {
              const fillInId = `fillIn-${Date.now()}-${Math.floor(
                Math.random() * 10000
              )}`;
              team[section] = {
                id: fillInId,
                firstName: "Fill-In",
                lastName: "Needed",
                fillIn: true,
                section,
              };
            }
          } else {
            const fillInId = `fillIn-${Date.now()}-${Math.floor(
              Math.random() * 10000
            )}`;
            team[section] = {
              id: fillInId,
              firstName: "Fill-In",
              lastName: "Needed",
              fillIn: true,
              section,
            };
          }
        });
        generatedTeams.push({ members: team });
      }

      const teamsColRef = collection(db, "Pennants", pennantId, "teams");
      await Promise.all(
        generatedTeams.map(async (team, index) => {
          const teamDocRef = await addDoc(teamsColRef, {
            name: teamNames[index] || `Team ${index + 1}`,
            members: team.members,
          });

          const fillInList = Object.values(team.members).filter((m) => m.fillIn);
          if (fillInList.length > 0) {
            const fillInRequiredRef = collection(
              db,
              "Pennants",
              pennantId,
              "fillInRequired"
            );
            await Promise.all(
              fillInList.map((fi) =>
                addDoc(fillInRequiredRef, {
                  createdAt: new Date(),
                  section: fi.section,
                  reason: "Team generation missing members",
                  teamId: teamDocRef.id,
                })
              )
            );
          }
        })
      );

      await refreshTeams();
      alert("Teams generated with fill-ins!");
    } catch (err) {
      console.error("Error generating teams:", err);
      alert("Failed to generate teams. Check console for details.");
    }
  };

  const handleRegenerateTeams = async () => {
    if (!pennantData || pennantData.participantType !== "Teams") {
      alert("Not a Teams event!");
      return;
    }
    try {
      const teamsSnap = await getDocs(collection(db, "Pennants", pennantId, "teams"));
      const deletions = teamsSnap.docs.map((td) =>
        deleteDoc(doc(db, "Pennants", pennantId, "teams", td.id))
      );
      await Promise.all(deletions);
      await generateTeams();
      await refreshTeams();
      alert("Teams regenerated!");
    } catch (err) {
      console.error("Error regenerating teams:", err);
      alert("Error regenerating teams.");
    }
  };

  const handleOpenScoreSheet = (weekDraw) => {
    setSelectedWeek(weekDraw);
    setOpenScoreSheet(true);
  };
  const handleCloseScoreSheet = () => {
    setOpenScoreSheet(false);
    setSelectedWeek(null);
  };

  const handleOpenPrintableScoreSheet = (weekDraw) => {
    const queryParams = new URLSearchParams({
      pennantId,
      weekData: JSON.stringify(weekDraw),
    });
    window.open(`/printable-scoresheet?${queryParams.toString()}`, "_blank");
  };
  const handleClosePrintableScoreSheet = () => {
    setOpenPrintableScoreSheet(false);
    setSelectedWeek(null);
  };

  const toggleEditTeams = () => setOpenEditTeams(!openEditTeams);


  const groupWeeks = (allWeeks) => {
    if (!allWeeks) return {};

    if (pennantData?.participantType === "Teams") {
      return { teams: allWeeks };
    }

    const grouped = {};
    allWeeks.forEach((w) => {
      const sec = w.section || "general";
      if (!grouped[sec]) grouped[sec] = [];
      grouped[sec].push(w);
    });
    return grouped;
  };

  return (
    <Container className="no-print">
      <Typography variant="h4" gutterBottom>
        Draws
      </Typography>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
           {pennantData?.participantType === "Teams" && (
            <Box sx={{ mb: 2 }}>
              {!teamsExist ? (
                <Button
                  variant="contained"
                  onClick={generateTeams}
                  sx={{ mt: 2 }}
                  disabled={!pennantId}
                >
                  Generate Teams
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={toggleEditTeams}
                  sx={{ mt: 2 }}
                  disabled={!pennantId}
                >
                  Edit Teams
                </Button>
              )}
            </Box>
          )}

          {pennantData?.participantType === "Individuals" &&
            pennantData?.playType === "Doubles" && (
              <Box sx={{ mb: 2 }}>
                {!teamsExist ? (
                  <Button
                    variant="contained"
                    onClick={toggleEditTeams}
                    sx={{ mt: 2 }}
                    disabled={!pennantId}
                  >
                    Create Doubles Pairs
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={toggleEditTeams}
                    sx={{ mt: 2 }}
                    disabled={!pennantId}
                  >
                    Edit Doubles Pairs
                  </Button>
                )}
              </Box>
            )}

            {existingDraw && existingDraw.weeks && (
              <Box sx={{ mt: 4 }}>
                <Typography variant="h4" gutterBottom>
                  {pennantData?.name}
                </Typography>

                {(() => {
                  const grouped = groupWeeks(existingDraw.weeks);

                  if (pennantData?.participantType === "Teams") {
                    return (
                      <TeamsDraw
                        groupedWeeks={grouped}
                        handleOpenScoreSheet={handleOpenScoreSheet}
                        handleOpenPrintableScoreSheet={handleOpenPrintableScoreSheet}
                      />
                    );
                  }
                  if (
                    pennantData?.participantType === "Individuals" &&
                    pennantData?.playType === "Doubles"
                  ) {
                    return (
                      <DoublesDraw
                        groupedWeeks={grouped}
                        handleOpenScoreSheet={handleOpenScoreSheet}
                        handleOpenPrintableScoreSheet={handleOpenPrintableScoreSheet}
                      />
                    );
                  }

                  return (
                    <SinglesDraw
                      groupedWeeks={grouped}
                      handleOpenScoreSheet={handleOpenScoreSheet}
                      handleOpenPrintableScoreSheet={handleOpenPrintableScoreSheet}
                    />
                  );
                })()}

<Button
                variant="contained"
                onClick={handleOpenEditDrawModal}
                sx={{ mt: 2 }}
              >
                Edit Draw
              </Button>
                <Button
                  variant="contained"
                  color="error"
                  onClick={deleteDraw}
                  sx={{ mt: 2, ml: 2 }}
                >
                  Delete Draw
                </Button>
              </Box>
            )}


          {!existingDraw && (
            <>
              <Button
                variant="contained"
                onClick={generateDraw}
                sx={{ mt: 2 }}
                disabled={!pennantId}
              >
                Generate Draw
              </Button>

              <Scrollbar>
                {draws.length > 0 && (
                  <Box sx={{ mt: 4 }}>
                    <Typography variant="h6" gutterBottom>
                      Generated Draws
                    </Typography>
                    <TableContainer component={Paper}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Week</TableCell>
                            <TableCell>Date</TableCell>
                            <TableCell>Matches</TableCell>
                            <TableCell />
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {draws.map((weekDraw, index) => (
                            <TableRow key={index}>
                              <TableCell>{weekDraw.week}</TableCell>
                              <TableCell>{weekDraw.date}</TableCell>
                              <TableCell>
                                {weekDraw.matches.map((match, idx) => (
                                  <Typography key={idx}>
                                    {match.team1?.name || "Team A"} vs{" "}
                                    {match.team2?.name || "Team B"}
                                  </Typography>
                                ))}
                              </TableCell>
                              <TableCell>
                                <Button
                                  variant="contained"
                                  onClick={() => handleOpenScoreSheet(weekDraw)}
                                >
                                  Add/Update Scores
                                </Button>
                                <Button
                                  variant="outlined"
                                  sx={{ ml: 1 }}
                                  onClick={() =>
                                    handleOpenPrintableScoreSheet(weekDraw)
                                  }
                                >
                                  Printable Score Sheet
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
              </Scrollbar>
            </>
          )}
        </>
      )}

      <Dialog
        open={openScoreSheet}
        onClose={handleCloseScoreSheet}
        maxWidth="md"
        fullWidth
      >
        {pennantData?.participantType === "Teams" ? (
  <TeamsScoreSheet pennantId={pennantId} weekData={selectedWeek} />
) : (
  <IndividualsScoreSheet pennantId={pennantId} weekData={selectedWeek} />
)}
      </Dialog>

      <Dialog
        open={openPrintableScoreSheet}
        onClose={handleClosePrintableScoreSheet}
        maxWidth="md"
        fullWidth
      >
        {pennantData && selectedWeek && (
          <PrintableScoreSheet
            pennantData={pennantData}
            weekData={selectedWeek}
          />
        )}
      </Dialog>

      <EditTeams
        isOpen={openEditTeams}
        toggle={toggleEditTeams}
        pennantId={pennantId}
        refreshTeams={refreshTeams}
      />

<EditDrawModal
        open={openEditDrawModal}
        onClose={() => setOpenEditDrawModal(false)}
        draw={existingDraw}
        availablePlayers={
          (pennantData?.playType === "Doubles" || pennantData?.participantType === "Teams")
            ? teams
            : members
        }
        onSave={handleSaveEditedDraw}
        playType={pennantData?.playType}
        participantType={pennantData?.participantType}
      />
    </Container>
  );
}

Draws.propTypes = {
  pennantId: PropTypes.string.isRequired,
  seasonId: PropTypes.string,
};
